import { useEffect, useState, useMemo } from "react";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { KanbanColumn } from "./KanbanColumn";
import { KanbanCard } from "./KanbanCard";
import type { ComplianceTask } from "@/hooks/use-compliance-tasks";

type KanbanStatus = ComplianceTask["status"];

const KANBAN_STATUS_IDS: readonly KanbanStatus[] = [
  "not_started",
  "in_progress",
  "at_risk",
  "blocked",
  "complete",
];

const KANBAN_COLUMNS: {
  id: KanbanStatus;
  label: string;
  color: string;
  badgeColor: string;
}[] = [
  { id: "not_started", label: "Not Started", color: "bg-gray-100 dark:bg-gray-900/40", badgeColor: "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300" },
  { id: "in_progress", label: "In Progress", color: "bg-blue-50 dark:bg-blue-950/20", badgeColor: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300" },
  { id: "at_risk",     label: "At Risk",     color: "bg-orange-50 dark:bg-orange-950/20", badgeColor: "bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300" },
  { id: "blocked",     label: "Blocked",     color: "bg-red-50 dark:bg-red-950/20", badgeColor: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300" },
  { id: "complete",    label: "Complete",    color: "bg-green-50 dark:bg-green-950/20", badgeColor: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300" },
];

function isKanbanStatus(value: unknown): value is KanbanStatus {
  return typeof value === "string" && KANBAN_STATUS_IDS.includes(value as KanbanStatus);
}

function getTaskStatus(task: ComplianceTask): KanbanStatus {
  return isKanbanStatus(task.status) ? task.status : "not_started";
}

function createColumnMap(): Record<KanbanStatus, ComplianceTask[]> {
  return {
    not_started: [],
    in_progress: [],
    at_risk: [],
    blocked: [],
    complete: [],
  };
}

interface KanbanBoardProps {
  tasks: ComplianceTask[];
  updateTask: (taskId: string, updates: Partial<ComplianceTask>) => Promise<unknown>;
  onTaskClick: (task: ComplianceTask) => void;
  getDepartmentColor: (id: string | null) => string;
  getDepartmentName: (id: string | null) => string;
}

function isOverdue(task: ComplianceTask) {
  if (!task.due_date || task.status === "complete") return false;
  return new Date(task.due_date) < new Date();
}

function getDaysUntilDue(due: string | null): number | null {
  if (!due) return null;
  const diffTime = new Date(due).getTime() - new Date().getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

export function KanbanBoard({ tasks: initialTasks, updateTask, onTaskClick, getDepartmentColor, getDepartmentName }: KanbanBoardProps) {
  const [tasks, setTasks] = useState<ComplianceTask[]>(initialTasks);
  const [activeTask, setActiveTask] = useState<ComplianceTask | null>(null);

  // Keep local tasks in sync when parent tasks change (e.g., from realtime)
  useEffect(() => {
    setTasks(initialTasks);
  }, [initialTasks]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const columnTasks = useMemo(() => {
    const map = createColumnMap();
    for (const task of tasks) {
      const status = getTaskStatus(task);
      map[status].push(task.status === status ? task : { ...task, status });
    }
    return map;
  }, [tasks]);

  function handleDragStart(event: DragStartEvent) {
    const task = tasks.find((t) => t.id === event.active.id);
    setActiveTask(task ? { ...task, status: getTaskStatus(task) } : null);
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    setActiveTask(null);
    if (!over) return;

    const overId = String(over.id);
    const overTask = tasks.find((t) => t.id === overId);
    const newStatus = isKanbanStatus(overId)
      ? overId
      : overTask
        ? getTaskStatus(overTask)
        : null;

    if (!newStatus) return;

    const task = tasks.find((t) => t.id === active.id);
    if (!task || getTaskStatus(task) === newStatus) return;

    // Optimistic update
    setTasks((prev) =>
      prev.map((t) => (t.id === task.id ? { ...t, status: newStatus } : t))
    );

    updateTask(task.id, { status: newStatus }).catch(() => {
      // Revert on error
      setTasks((prev) =>
        prev.map((t) => (t.id === task.id ? { ...t, status: task.status } : t))
      );
    });
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-4 overflow-x-auto pb-4 px-4 pt-3">
        {KANBAN_COLUMNS.map((column) => (
          <KanbanColumn
            key={column.id}
            column={column}
            tasks={columnTasks[column.id]}
            onTaskClick={onTaskClick}
            getDepartmentColor={getDepartmentColor}
            getDepartmentName={getDepartmentName}
            isOverdue={isOverdue}
            getDaysUntilDue={getDaysUntilDue}
          />
        ))}
      </div>

      <DragOverlay>
        {activeTask && (
          <KanbanCard
            task={activeTask}
            onClick={() => {}}
            departmentColor={getDepartmentColor(activeTask.department_id)}
            departmentName={getDepartmentName(activeTask.department_id)}
            isOverdue={isOverdue(activeTask)}
            daysUntilDue={getDaysUntilDue(activeTask.due_date)}
          />
        )}
      </DragOverlay>
    </DndContext>
  );
}
